/**
 * A run as a replay plays it, for any story: the run's status events in the
 * shapes the executor emits, the answer it streamed, and the trace lines and
 * progress line the application derives from them. The derived lines are
 * stored because the derivation reads @mlx/contracts at runtime, which the
 * marketing bundle carries as types only; each story's test re-derives them
 * with the application's own functions at every point of the run.
 *
 * Plain data, so a server-rendered first frame can hand a story to the
 * player; the player builds the events from it.
 */

import type { TaskRunEvent } from "@mlx/contracts";
import type { RenderedRunEventBlock } from "@product/lib/task-run-ui";

/** One event before the answer: [kind, run ms, payload]. */
export type ReplayStep = readonly [kind: string, atMs: number, payload: Record<string, unknown>];

/** A trace line as getRenderedRunEventBlocks prints a status event. */
export type ReplayStatusLine = {
  title: string;
  description: string;
  tone: RenderedRunEventBlock["tone"];
};

/** The transcript's progress line after an event, as getRunLiveUpdates words it. */
export type ReplayLiveUpdate = { id: string; atMs: number; text: string };

export type ReplayRun = {
  id: string;
  /** Event ids are `<eventPrefix>_<seq>`, numbered from 001 in run order. */
  eventPrefix: string;
  startedAt: number;
  durationMs: number;
  /** Queued, claimed and the tool calls, before the answer streams. */
  steps: readonly ReplayStep[];
  /** When the answer starts and stops streaming, in run ms. */
  stream: { startsMs: number; endsMs: number };
  /** The printed line for each status event that prints one, by event id. */
  statusLines: Readonly<Record<string, ReplayStatusLine>>;
  liveUpdates: readonly ReplayLiveUpdate[];
};

/** The replay's clock against the run's, and when the record opens and the replay ends. */
export type ReplayTimeline = {
  /** [replay ms, run ms] keyframes, eased linearly between. */
  keyframes: readonly (readonly [number, number])[];
  recordOpensMs: number;
  endsMs: number;
  /** Where the transcript looks from each moment on; it follows the live edge until the first. */
  cues?: readonly (readonly [atMs: number, camera: ReplayCamera])[];
  /** When the open record's sheet scrolls to its last field, where it outgrows the frame (a phone). */
  sheetScrollMs?: number;
};

/**
 * Where the transcript looks: at the live edge as the answer grows, as the
 * app does; at the answer's opening; at its table; or at the table swiped to
 * its last column, as a reader on a phone scrolls and swipes.
 */
export type ReplayCamera = "follow" | "answer" | "table" | "swipe";

/**
 * The replay on a phone, where the answer and its table outgrow the frame:
 * once the run completes, the transcript returns to the answer's opening,
 * then shows the table, swipes it to its last column, then the record
 * opens and, once its first fields are read, scrolls to its next step;
 * each held long enough to read.
 */
export function phoneTimeline(timeline: ReplayTimeline): ReplayTimeline {
  const completeMs = timeline.keyframes.at(-1)![0];
  const recordOpensMs = completeMs + 6_400;
  return {
    keyframes: timeline.keyframes,
    recordOpensMs,
    sheetScrollMs: recordOpensMs + 2_400,
    endsMs: recordOpensMs + 5_800,
    cues: [
      [completeMs + 400, "answer"],
      [completeMs + 3_200, "table"],
      [completeMs + 4_600, "swipe"],
    ],
  };
}

/** Where the transcript looks at `atMs` into the replay. */
export function replayCameraAt(timeline: ReplayTimeline, atMs: number): ReplayCamera {
  return [...(timeline.cues ?? [])].reverse().find(([cueMs]) => cueMs <= atMs)?.[1] ?? "follow";
}

/** A provider tool's result as the runtime returns it: JSON text. */
export function jsonResult(value: unknown) {
  return { content: [{ type: "text", text: JSON.stringify(value, null, 2) }] };
}

/** The answer as the model streamed it, a word at a time. */
export function answerDeltas(answerMarkdown: string): readonly string[] {
  return answerMarkdown.match(/\S+\s*|\s+/g) ?? [];
}

/**
 * The run's events: its steps, the answer's deltas spread evenly across the
 * stream, then completion with the answer as the run's output.
 */
export function replayEvents(run: ReplayRun, answerMarkdown: string): TaskRunEvent[] {
  const deltas = answerDeltas(answerMarkdown);
  const { startsMs, endsMs } = run.stream;
  const steps: ReplayStep[] = [
    ...run.steps,
    ...deltas.map((delta, index): ReplayStep => [
      "assistant.part.delta",
      startsMs + Math.round(((endsMs - startsMs) * index) / Math.max(1, deltas.length - 1)),
      { partId: "part_answer", partType: "text", delta },
    ]),
    ["run.completed", run.durationMs, { outputText: answerMarkdown }],
  ];
  return steps.map(
    ([kind, atMs, payload], index) =>
      ({
        id: `${run.eventPrefix}_${String(index + 1).padStart(3, "0")}`,
        runId: run.id,
        seq: index + 1,
        createdAt: run.startedAt + atMs,
        kind,
        payload,
      }) as TaskRunEvent,
  );
}

/** The trace's lines once the run is `atMs` in, as TaskRunActivity shows them. */
export function replayBlocksAt(run: ReplayRun, events: readonly TaskRunEvent[], atMs: number): RenderedRunEventBlock[] {
  const blocks: RenderedRunEventBlock[] = [];
  const deltas = events.filter(
    (event) => event.kind === "assistant.part.delta" && event.createdAt <= run.startedAt + atMs,
  );
  for (const event of events) {
    if (event.createdAt > run.startedAt + atMs) break;
    if (event.kind === "assistant.part.delta") {
      if (event !== deltas[0]) continue;
      blocks.push({
        id: event.id,
        type: "assistant-stream",
        kind: "assistant.part.delta",
        partType: "text",
        title: "Streaming response",
        description: `${deltas.length} delta${deltas.length === 1 ? "" : "s"} received from the model.`,
        createdAt: event.createdAt,
        tone: "info",
        text: deltas.map((delta) => String((delta.payload as { delta?: unknown }).delta ?? "")).join(""),
        deltaCount: deltas.length,
      });
      continue;
    }
    const line = run.statusLines[event.id];
    if (!line) continue;
    blocks.push({
      id: event.id,
      type: "status",
      kind: event.kind,
      ...line,
      createdAt: event.createdAt,
      payload: event.payload as Record<string, unknown>,
    });
  }
  return blocks;
}

/** The newest progress line once the run is `atMs` in, or none once the answer streams. */
export function replayLiveUpdateAt(run: ReplayRun, atMs: number): ReplayLiveUpdate | null {
  if (atMs >= run.stream.startsMs) return null;
  return [...run.liveUpdates].reverse().find((update) => update.atMs <= atMs) ?? null;
}

/** The answer as far as it has streamed once the run is `atMs` in. */
export function replayAnswerAt(run: ReplayRun, events: readonly TaskRunEvent[], atMs: number): string {
  return events
    .filter((event) => event.kind === "assistant.part.delta" && event.createdAt <= run.startedAt + atMs)
    .map((event) => String((event.payload as { delta?: unknown }).delta ?? ""))
    .join("");
}

/** The run's clock at `atMs` into the replay. */
export function replayRunClockAt(timeline: ReplayTimeline, run: ReplayRun, atMs: number): number {
  const { keyframes } = timeline;
  for (let index = 1; index < keyframes.length; index += 1) {
    const [t1, r1] = keyframes[index]!;
    const [t0, r0] = keyframes[index - 1]!;
    if (atMs <= t1) return r0 + ((r1 - r0) * Math.max(0, atMs - t0)) / (t1 - t0);
  }
  return run.durationMs;
}
